/**
 * SeoHead — Reusable Next.js head tag component for programmatic SEO.
 *
 * Usage (in any client or server component):
 *   <SeoHead title="..." description="..." canonical="https://seasoftclasses.com/courses/power-bi" schema={schemaObject} />
 *
 * Note: This uses next/head which works in client components in the App Router.
 * For static page metadata, prefer the `metadata` export in page.tsx files.
 * HelmetProvider is NOT required — this project uses Next.js, not Create React App.
 */

"use client";

import Head from "next/head";
import { usePathname } from "next/navigation";
import { defaultSEO } from "@/config/seoConfig";
import OgImageMeta from "@/components/SEO/OgImageMeta";

interface SeoHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  currentPath?: string;
  noIndex?: boolean;
  preloadImages?: string[];
  schema?: object | object[];
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = "website",
  currentPath,
  noIndex = false,
  preloadImages,
  schema,
}: SeoHeadProps) {
  const pathname = usePathname();
  const resolvedTitle = title || defaultSEO.defaultTitle;
  const resolvedDescription = description || defaultSEO.defaultDescription;
  const resolvedKeywords = keywords || defaultSEO.defaultKeywords;
  const resolvedCanonical = canonical || defaultSEO.siteUrl;
  const resolvedPath = currentPath || pathname || "/";
  const normalizedPath = resolvedPath.startsWith("/") ? resolvedPath : `/${resolvedPath}`;
  const alternateHref = `${defaultSEO.siteUrl}${normalizedPath}`;

  const robotsContent = noIndex ? "noindex, nofollow" : "index, follow";

  // Normalise schema — accept a single object or an array
  const schemaArray: object[] | null = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : null;

  return (
    <Head>
      {/* Primary */}
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={resolvedKeywords} />
      <meta name="google-site-verification" content={defaultSEO.googleVerification} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={resolvedCanonical} />
      <link key="alt-en-us" rel="alternate" hrefLang="en-us" href={alternateHref} />
      <link key="alt-en-in" rel="alternate" hrefLang="en-in" href={alternateHref} />
      <link key="alt-en-ca" rel="alternate" hrefLang="en-ca" href={alternateHref} />
      <link key="alt-en-gb" rel="alternate" hrefLang="en-gb" href={alternateHref} />
      <link key="alt-x-default" rel="alternate" hrefLang="x-default" href={alternateHref} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="prefetch" href="/courses" />
      <link rel="prefetch" href="/contact" />
      <link rel="prefetch" href="/blog" />
      {(preloadImages || []).map((imageSrc) => {
        const href = imageSrc.startsWith("http")
          ? imageSrc
          : imageSrc.startsWith("/")
            ? `${defaultSEO.siteUrl}${imageSrc}`
            : `${defaultSEO.siteUrl}/${imageSrc}`;

        return <link key={`preload-${href}`} rel="preload" as="image" href={href} />;
      })}

      {/* Open Graph */}
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={resolvedCanonical} />
      <OgImageMeta title={resolvedTitle} ogImage={ogImage} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={defaultSEO.locale} />

      {/*  X (formerly Twitter) Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />

      {/* JSON-LD Structured Data */}
      {schemaArray &&
        schemaArray.map((s, i) => (
          <script
            key={i}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
          />
        ))}
    </Head>
  );
}
